import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Typography, Button, Card, Row, Col, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const AggregationNodeProperties = ({ config, form, inputSchema }) => {
  const [aggregations, setAggregations] = useState(config.aggregations || []);

  // Update form when aggregations change
  useEffect(() => {
    form.setFieldsValue({
      aggregations: aggregations
    });
  }, [aggregations, form]);

  // Add new aggregation
  const addAggregation = () => {
    const newAggregation = {
      id: `agg_${Date.now()}`,
      function: 'sum',
      column: '',
      alias: '',
      filter: ''
    };
    setAggregations([...aggregations, newAggregation]);
  };

  // Remove aggregation
  const removeAggregation = (index) => {
    const newAggregations = aggregations.filter((_, i) => i !== index);
    setAggregations(newAggregations);
  };

  // Update aggregation
  const updateAggregation = (index, updates) => {
    const newAggregations = [...aggregations];
    newAggregations[index] = { ...newAggregations[index], ...updates };
    setAggregations(newAggregations);
  };

  return (
    <>
      <Form.Item label="Group By Columns" name="groupBy" initialValue={config.groupBy || []}>
        <Select mode="tags" placeholder="Select columns to group by (optional)">
          {inputSchema && inputSchema.map(col => (
            <Option key={col.name} value={col.name}>{col.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Aggregation Functions</Divider>
      
      {/* Hidden form field to store aggregations in form */}
      <Form.Item name="aggregations" initialValue={aggregations} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Aggregations List */}
      <div style={{ marginBottom: 16 }}>
        {aggregations.map((agg, index) => (
          <Card 
            key={agg.id}
            size="small" 
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{agg.alias || `${agg.function}(${agg.column || '?'})`}</Text>
                <Button 
                  type="text" 
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeAggregation(index)}
                />
              </div>
            }
          >
            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Function</Text>
                  <Select 
                    size="small"
                    style={{ width: '100%' }}
                    value={agg.function}
                    onChange={(value) => updateAggregation(index, { function: value })}
                  >
                    <Option value="sum">Sum</Option>
                    <Option value="avg">Average</Option>
                    <Option value="min">Minimum</Option>
                    <Option value="max">Maximum</Option>
                    <Option value="count">Count</Option>
                    <Option value="count_distinct">Count Distinct</Option>
                    <Option value="std">Standard Deviation</Option>
                    <Option value="var">Variance</Option>
                    <Option value="first">First</Option>
                    <Option value="last">Last</Option>
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Column</Text>
                  {inputSchema && inputSchema.length > 0 ? (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={agg.column}
                      onChange={(value) => updateAggregation(index, { column: value })}
                      placeholder="Select column"
                      showSearch
                    >
                      {inputSchema.map(col => (
                        <Option key={col.name} value={col.name}>{col.name}</Option>
                      ))}
                    </Select>
                  ) : (
                    <Input 
                      size="small"
                      value={agg.column}
                      onChange={(e) => updateAggregation(index, { column: e.target.value })}
                      placeholder="Column name"
                    />
                  )}
                </div>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Alias (Result Name)</Text>
                  <Input 
                    size="small"
                    value={agg.alias}
                    onChange={(e) => updateAggregation(index, { alias: e.target.value })}
                    placeholder="Result column name"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Filter (Optional)</Text>
                  <Input 
                    size="small"
                    value={agg.filter}
                    onChange={(e) => updateAggregation(index, { filter: e.target.value })}
                    placeholder="e.g., column > 100"
                  />
                </div>
              </Col>
            </Row>
          </Card>
        ))}
      </div>

      {/* Add New Aggregation Button */}
      <Button 
        type="dashed" 
        onClick={addAggregation}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Aggregation Function
      </Button>

      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px' }}>
        <Text type="secondary">
          <strong>Aggregation Node:</strong><br/>
          • Applies multiple aggregation functions to dataset<br/>
          • Group By: Columns to group data (optional)<br/>
          • Each function creates a new result column<br/>
          • Filters can be applied to individual aggregations
        </Text>
      </div>
    </>
  );
};

export default AggregationNodeProperties;